import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Vote, Plus } from "lucide-react";
import { track } from "@/lib/analytics";

const VOTED_KEY = "titans_polls_voted";

function getVoted() {
  try {
    return JSON.parse(localStorage.getItem(VOTED_KEY) || "{}");
  } catch {
    return {};
  }
}

function setVoted(map) {
  localStorage.setItem(VOTED_KEY, JSON.stringify(map));
}

function PollCard({ poll, onVote, votedOptionId }) {
  const total = (poll.options || []).reduce((s, o) => s + (o.votes || 0), 0);
  const hasVoted = !!votedOptionId;

  return (
    <div data-testid={`poll-card-${poll.id}`} className="titans-card p-6">
      <div className="flex items-center gap-2 mb-2">
        <Vote size={14} className="text-titans-blue" />
        <span className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue">Fan Poll</span>
      </div>
      <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide text-white">{poll.question}</h3>
      <div className="text-titans-muted text-[10px] font-cond uppercase tracking-wider mt-1">
        {poll.created_at ? formatDate(poll.created_at) : ""} · {total} votes
      </div>
      <div className="mt-5 space-y-3">
        {(poll.options || []).map((o) => {
          const pct = total ? Math.round(((o.votes || 0) / total) * 100) : 0;
          const isMine = votedOptionId === o.id;
          return (
            <button
              key={o.id}
              data-testid={`poll-${poll.id}-option-${o.id}`}
              onClick={() => !hasVoted && onVote(poll.id, o.id)}
              disabled={hasVoted}
              className={`w-full text-left relative overflow-hidden border ${
                isMine ? "border-titans-red" : "border-[rgba(138,141,143,0.3)]"
              } ${hasVoted ? "cursor-default" : "hover:border-titans-blue"} transition-colors`}
            >
              <div
                className={`absolute inset-y-0 left-0 ${isMine ? "bg-titans-red/30" : "bg-titans-elev"}`}
                style={{ width: hasVoted ? `${pct}%` : "0%", transition: "width 400ms ease" }}
              />
              <div className="relative flex items-center justify-between px-4 py-3">
                <span className="font-cond uppercase tracking-wider text-sm text-white">{o.label}</span>
                <span className="font-display text-lg text-white">
                  {hasVoted ? `${pct}%` : "Vote"}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {hasVoted && (
        <div className="mt-4 text-titans-silver text-xs font-cond uppercase tracking-wider">
          Thanks for voting.
        </div>
      )}
    </div>
  );
}

export default function Polls() {
  return (
    <div data-testid="polls-page" className="max-w-[1100px] mx-auto px-5 md:px-8 py-16">
      <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">Fan Voice</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide section-title">Polls</h1>
          <p className="text-titans-silver mt-4 max-w-xl font-body">
            Drop your vote. Watch the locker room react in real time.
          </p>
        </div>
        <Link
          to="/admin"
          data-testid="polls-admin-link"
          className="self-start md:self-auto border-2 border-titans-blue text-titans-blue hover:bg-titans-blue hover:text-white px-5 py-3 font-cond uppercase tracking-wider text-sm inline-flex items-center gap-2 transition-colors"
        >
          <Plus size={14} /> Create Poll
        </Link>
      </div>

      <PollsBoard />
    </div>
  );
}

export function PollsBoard() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voted, setVotedState] = useState(getVoted());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/polls");
      setPolls(data.polls || []);
    } catch (e) {
      console.error("Polls load failed:", e);
      toast.error("Failed to load polls");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vote = async (pollId, optionId) => {
    if (voted[pollId]) return;
    try {
      await api.post(`/polls/${pollId}/vote`, { option_id: optionId });
      track("poll_vote", { poll_id: pollId });
      const next = { ...voted, [pollId]: optionId };
      setVoted(next);
      setVotedState(next);
      toast.success("Vote counted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    }
  };

  if (loading) return <div className="text-titans-silver">Loading polls…</div>;
  if (polls.length === 0) {
    return (
      <div className="titans-card p-10 text-center">
        <BarChart3 size={36} className="mx-auto text-titans-red" />
        <div className="font-display text-3xl uppercase tracking-wide mt-4">No polls yet.</div>
        <div className="text-titans-silver mt-2 font-body">
          Open the Owner Console to create your first fan poll.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="polls-board">
      {polls.map((p) => (
        <PollCard key={p.id} poll={p} onVote={vote} votedOptionId={voted[p.id]} />
      ))}
    </div>
  );
}
